// Warnings for logic objects created from templates. At template save time,
// account-specific settings are cleared and the raw keys are recorded in the
// object's data/options "template_unset" marker (see CtaBuilder#record_template_unset);
// each warning self-heals once every listed setting is configured.
class TemplateElementWarningsService {
  static humanizeTemplateField(key) {
    const label = String(key)
      .replace(/^condition_/, "")
      .replace(/[-_]ids?$/, "")
      .replace(/_/g, " ");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  static isRefBlank(value) {
    return !value || value.toString() === "0";
  }

  static getObjectWarning(object) {
    const marker =
      object?.data?.["template_unset"] || object?.options?.["template_unset"];
    if (!marker) return undefined;

    const blankKeys = marker
      .split(",")
      .filter((key) => key)
      .filter((key) =>
        this.isRefBlank(
          object[key] ?? object.data?.[key] ?? object.options?.[key]
        )
      );
    if (blankKeys.length === 0) return undefined;

    return `Automation settings incomplete: ${blankKeys
      .map((key) => this.humanizeTemplateField(key))
      .join(", ")}`;
  }

  static getActionGroupWarning(actionGroup) {
    if (!actionGroup || actionGroup.toBeDeleted) return undefined;
    if (!actionGroup.data?.["redirect-step-position"]) return undefined;
    if (!this.isRefBlank(actionGroup.data?.["redirect-step"])) return undefined;

    return "Step to jump to not yet set";
  }

  static getContactableChildWarning(objects, contactableType, contactableId) {
    return Object.values(objects || {})
      .filter(
        (object) =>
          object.contactable_type == contactableType &&
          object.contactable_id == contactableId &&
          object.toBeDeleted !== true
      )
      .map((object) => this.getObjectWarning(object))
      .find(Boolean);
  }

  static getActionGroupRollupWarning(actionGroup, variant) {
    if (!actionGroup || actionGroup.toBeDeleted) return undefined;

    return (
      this.getActionGroupWarning(actionGroup) ||
      this.getContactableChildWarning(
        variant.actions,
        "ActionGroup",
        actionGroup.id
      ) ||
      this.getContactableChildWarning(
        variant.conditions,
        "ActionGroup",
        actionGroup.id
      )
    );
  }

  static getReviewRecommendationRollupWarning(reviewRecommendation, variant) {
    if (!reviewRecommendation || reviewRecommendation.toBeDeleted)
      return undefined;

    return (
      this.getObjectWarning(reviewRecommendation) ||
      this.getContactableChildWarning(
        variant.conditions,
        "ReviewRecommendation",
        reviewRecommendation.id
      )
    );
  }

  static getSchedulerRecommendationRollupWarning(schedulerRecommendation, variant) {
    if (!schedulerRecommendation || schedulerRecommendation.toBeDeleted)
      return undefined;

    return (
      this.getObjectWarning(schedulerRecommendation) ||
      this.getContactableChildWarning(
        variant.conditions,
        "SchedulerRecommendation",
        schedulerRecommendation.id
      )
    );
  }

  static getEmbedRecommendationRollupWarning(embedRecommendation, variant) {
    if (!embedRecommendation || embedRecommendation.toBeDeleted)
      return undefined;

    return (
      this.getObjectWarning(embedRecommendation) ||
      this.getContactableChildWarning(
        variant.conditions,
        "EmbedRecommendation",
        embedRecommendation.id
      )
    );
  }

  static getElementActionsWarning(element, variant) {
    return Object.values(variant.actions || {})
      .filter(
        (action) =>
          action.element_id == element.id &&
          action.contactable_type == "Element" &&
          action.toBeDeleted !== true
      )
      .map((action) => this.getObjectWarning(action))
      .find(Boolean);
  }

  static getElementRollupWarning(element, variant) {
    if (!element || element.toBeDeleted) return undefined;

    return (
      this.getElementActionsWarning(element, variant) ||
      Object.values(variant.action_groups || {})
        .filter((group) => {
          if (group.toBeDeleted) return false;

          const ownerElementId =
            group.element_id || variant.fields?.[group.field_id]?.element_id;
          return ownerElementId == element.id;
        })
        .map((group) => this.getActionGroupRollupWarning(group, variant))
        .find(Boolean) ||
      Object.values(variant.review_recommendations || {})
        .filter(
          (reviewRecommendation) =>
            reviewRecommendation.element_id == element.id
        )
        .map((reviewRecommendation) =>
          this.getReviewRecommendationRollupWarning(
            reviewRecommendation,
            variant
          )
        )
        .find(Boolean) ||
      Object.values(variant.scheduler_recommendations || {})
        .filter(
          (schedulerRecommendation) =>
            schedulerRecommendation.element_id == element.id
        )
        .map((schedulerRecommendation) =>
          this.getSchedulerRecommendationRollupWarning(
            schedulerRecommendation,
            variant
          )
        )
        .find(Boolean) ||
      Object.values(variant.embed_recommendations || {})
        .filter(
          (embedRecommendation) =>
            embedRecommendation.element_id == element.id
        )
        .map((embedRecommendation) =>
          this.getEmbedRecommendationRollupWarning(embedRecommendation, variant)
        )
        .find(Boolean)
    );
  }

  static elementHasWarning(element, variant) {
    return !!this.getElementRollupWarning(element, variant);
  }
}

if (typeof window !== "undefined" && typeof window.convertflow == "undefined") {
  window.TemplateElementWarningsService = TemplateElementWarningsService;
}

export default TemplateElementWarningsService;
